import React, { useMemo, useCallback } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import {
  AlignItems,
  Box,
  Display,
  FontWeight,
  Icon,
  JustifyContent,
  withCitrusTheme,
  Text,
} from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'
import { browserHistory } from 'react-router'
import { useLocation } from 'react-use'

import { client } from 'config/routes'
import { useCollection, useCurrentCollectionId } from 'hooks/menu-collections'
import { CollectionSlug } from 'routes/Menu/domains/collections'
import { getIsAuthenticated } from 'selectors/auth'

const NEW_NAV_COLLECTIONS = [
  CollectionSlug.Recommendations,
  CollectionSlug.Browse,
  CollectionSlug.AllRecipes,
]

const BUTTON_CONFIG = {
  browse: {
    action: 'back_to_browse_button_clicked',
    getPath: (pathname?: string) => ({
      pathname,
      search: '?collection=browse',
    }),
  },
  delivery: {
    action: 'back_to_delivery_details_button_clicked',
    getPath: () => client.deliveryDetails,
  },
}

type BackButtonLinkProps = {
  label: string
  onClick: () => void
  onKeyDown: (event: React.KeyboardEvent) => void
}

const onSpaceOrEnter =
  (callback: (event: React.KeyboardEvent) => void) => (event: React.KeyboardEvent) => {
    if (event.code === 'Space' || event.code === 'Enter') {
      event.preventDefault()
      callback(event)
    }
  }

export const useBackButton = () => {
  const { pathname } = useLocation()
  const currentCollectionId = useCurrentCollectionId()
  const currentCollection = useCollection(currentCollectionId)
  const isAuthenticated = useSelector(getIsAuthenticated)

  const currentSlug = currentCollection?.slug ?? CollectionSlug.AllRecipes

  const shouldGoToBrowse = isAuthenticated && !NEW_NAV_COLLECTIONS.includes(currentSlug)

  const buttonConfig = shouldGoToBrowse ? BUTTON_CONFIG.browse : BUTTON_CONFIG.delivery

  const onClick = useCallback(() => {
    datadogRum.addAction(buttonConfig.action)
    browserHistory.push(buttonConfig.getPath(pathname))
  }, [buttonConfig, pathname])

  return { label: 'Back', onClick }
}

const LinkButton = withCitrusTheme(Box, () => ({
  cursor: 'pointer',
}))

const BackButton = ({ label, onClick, onKeyDown }: BackButtonLinkProps) => {
  return (
    <LinkButton
      aria-label={`${label} Button`}
      data-testid="back-button-link"
      display={Display.Flex}
      alignItems={AlignItems.Center}
      justifyContent={JustifyContent.Center}
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
    >
      <Icon name="arrow_back" />
      <Text fontWeight={FontWeight.Bold} size={4}>
        {label}
      </Text>
    </LinkButton>
  )
}

export const BackButtonWrapper = () => {
  const { label, onClick } = useBackButton()
  const handleKeyDown = useMemo(() => onSpaceOrEnter(onClick), [onClick])

  return <BackButton label={label} onClick={onClick} onKeyDown={handleKeyDown} />
}
